import { CSSProperties, ReactNode } from 'react';
import { CmsMeta } from '../cmsMeta';
import './CmsSplitPanel.css';

/*
 * The proportions a split can take, as grid tracks. A list rather than any "a/b", so content
 * cannot ask for a split the layout was never looked at in; an unknown one falls back to even.
 */
const RATIOS: Record<string, string> = {
	'50/50': '1fr 1fr',
	'60/40': '3fr 2fr',
	'40/60': '2fr 3fr',
	'65/35': '13fr 7fr',
	'35/65': '7fr 13fr',
};

// the site's own palette by name, so a panel cannot drift from it; anything else is white
const BACKGROUNDS = [
	'white',
	'grey',
	'light-green',
	'green',
	'dark-green',
	'light-blue',
	'blue',
	'dark-blue',
	'light-indigo',
	'light-violet',
	'light-lilac',
	'light-pink',
	'light-coral',
	'light-orange',
	'light-yellow',
] as const;

// these carry white text; every other colour is light enough for the usual black
const DARK_BACKGROUNDS = new Set(['dark-green', 'dark-blue']);

/*
 * The steps each size takes. Content arrives as json nobody type-checked, so a value outside
 * these falls back to the default rather than to a class the stylesheet has no rule for.
 */
const GAPS = ['none', 'sm', 'md'] as const;
const RADII = ['none', 'sm', 'md', 'lg'] as const;
const PADDINGS = ['sm', 'md', 'lg'] as const;

function oneOf<T extends string>(allowed: readonly T[], value: string | undefined, fallback: T): T {
	return (allowed as readonly string[]).includes(value ?? '') ? (value as T) : fallback;
}

type CmsSplitPanelProps = {
	ratio?: string;
	gap?: (typeof GAPS)[number];
	radius?: (typeof RADII)[number];
	padding?: (typeof PADDINGS)[number];
	children: ReactNode;
};

/**
 * Two panels side by side that read as one block: same height, same corners, same padding. The
 * panels are CmsPanel children, which carry their own background; where the space it is given
 * is narrow they stack.
 */
export const CmsSplitPanel = ({ ratio = '50/50', gap, radius, padding, children }: CmsSplitPanelProps): JSX.Element => (
	// the outer box is what the layout measures, so the split follows the space it is given
	<div className="cms-split-panel">
		<div
			className={[
				'cms-split-panel__grid',
				`cms-split-panel--gap-${oneOf(GAPS, gap, 'sm')}`,
				`cms-split-panel--radius-${oneOf(RADII, radius, 'lg')}`,
				`cms-split-panel--padding-${oneOf(PADDINGS, padding, 'md')}`,
			].join(' ')}
			style={{ '--split-columns': RATIOS[ratio] ?? RATIOS['50/50'] } as CSSProperties}
		>
			{children}
		</div>
	</div>
);

type CmsPanelProps = {
	background?: string;
	align?: 'top' | 'center';
	ctaAtBottom?: boolean;
	children: ReactNode;
};

export const CmsPanel = ({ background = 'white', align = 'top', ctaAtBottom = false, children }: CmsPanelProps) => {
	const colour = (BACKGROUNDS as readonly string[]).includes(background) ? background : 'white';
	return (
		<div
			className={[
				'cms-panel',
				`cms-panel--${align}`,
				DARK_BACKGROUNDS.has(colour) ? 'cms-panel--dark' : '',
				ctaAtBottom ? 'cms-panel--cta-at-bottom' : '',
			]
				.filter(Boolean)
				.join(' ')}
			style={{ '--panel-background': `var(--color-${colour})` } as CSSProperties}
		>
			{children}
		</div>
	);
};

export const cmsMetas: Record<string, CmsMeta> = {
	CmsSplitPanel: {
		description:
			'Two panels side by side that read as one block - the same height, corners and padding - for key figures, proof and short offers: markets and customers, bonus, delivery, customer service. Its children are two CmsPanel. `ratio` is 50/50, 60/40, 40/60, 65/35 or 35/65, the first panel first. `gap` is none, sm or md between the panels; `radius` none, sm, md or lg; `padding` sm, md or lg inside each. The panels stack on a narrow screen. For a picture beside text use CmsStory instead.',
		example: [
			'CmsSplitPanel',
			{ ratio: '60/40', gap: 'sm', radius: 'lg', padding: 'lg' },
			[
				'CmsPanel',
				{ background: 'white' },
				[
					'CmsHtml',
					{
						content:
							'<h2>Kontorsgiganten är en del av Ondio</h2><p>Samma konto, samma kundservice och samma bonus - med större räckvidd i fem europeiska marknader.</p>',
					},
				],
				[
					'CheckmarkList',
					{},
					['li', {}, 'Sverige'],
					['li', {}, 'Norge'],
					['li', {}, 'Danmark'],
					['li', {}, 'Nederländerna'],
					['li', {}, 'Tyskland'],
				],
			],
			[
				'CmsPanel',
				{ background: 'light-green', ctaAtBottom: true },
				['CmsKpi', { value: '700 000+', label: 'kunder', note: 'i fem europeiska marknader' }],
				['CmsButton', { linkTo: '/about.php' }, 'Läs mer om oss'],
			],
		],
	},
	CmsPanel: {
		description:
			'One panel of a CmsSplitPanel, holding anything: CmsHtml for a heading and text, CmsKpi for a big figure, a CheckmarkList, a CmsImage, a CmsButton. `background` is one of the site colours by name - white, grey, light-green, green, dark-green, light-blue, blue, dark-blue, light-indigo, light-violet, light-lilac, light-pink, light-coral, light-orange or light-yellow - and text turns white on the dark ones. `align` is top or center. `ctaAtBottom` pins the last child to the bottom of the panel, so buttons line up across the two panels.',
		example: [
			'CmsPanel',
			{ background: 'light-green', align: 'top', ctaAtBottom: true },
			['CmsKpi', { value: '700 000+', label: 'kunder', note: 'i fem europeiska marknader' }],
			['CmsButton', { linkTo: '/about.php' }, 'Läs mer om oss'],
		],
	},
};
